/**
 * Dois catálogos globais do diagrama: a biblioteca de componentes e os
 * posicionamentos padrão.
 *
 * **Biblioteca de componentes** é a lista de nomes de que o desenho se serve
 * quando alguém troca um componente. Em produção são 500 na listagem sem busca —
 * e o acervo está sujo: um registro em branco, e a maioria dos nomes com espaços
 * sobrando na frente e atrás, que vieram de colagem. A tela mostra isso em vez de
 * aparar calada, porque aparar na tela esconderia o que está gravado.
 *
 * **Posicionamentos padrão** dizem onde um componente entra no desenho de um
 * sistema — apelido, cor, posição e ordem. **Quase não têm dado**: dos 14
 * sistemas que sondei, só o 33 tem alguma coisa, e são dois registros. Não é
 * defeito da tela; é o estado da tabela.
 *
 * Os dois são **dado global**: a trava recusa gravação em desenvolvimento.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "./cliente"
import type { OrdemDaLista } from "@/kit/ordemDaLista"

// -------------------------------------------------------------- biblioteca

export type ComponenteDaBiblioteca = { id: number; componente: string }

/**
 * A biblioteca corta em 500 no servidor, então a ordem vai para a API: ordenar aqui
 * só reordenaria o recorte, e o recém-incluído podia nem estar nele.
 */
export function usarBiblioteca(buscar: string, ordem: OrdemDaLista = "alfabetica") {
  return useQuery({
    queryKey: ["biblioteca-componentes", buscar, ordem] as const,
    staleTime: 60_000,
    queryFn: () => obter<ComponenteDaBiblioteca[]>(
      "api/biblioteca-componentes", { buscar: buscar || undefined, ordem }),
  })
}

/**
 * Confere se a descrição já existe antes de criar.
 *
 * É uma rota própria da API e não uma busca na lista porque a lista **corta em
 * 500**: procurar duplicata no que veio para a tela acharia "não existe" para
 * algo que existe na linha 600.
 */
export function usarConferirBiblioteca(descricao: string) {
  const texto = descricao.trim()
  return useQuery({
    queryKey: ["biblioteca-conferir", texto] as const,
    enabled: texto.length >= 3,
    staleTime: 30_000,
    queryFn: () => obter<{ existe: boolean }>("api/biblioteca-componentes/conferir", { descricao: texto }),
  })
}

export function usarSalvarBiblioteca() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, componente }: { id?: number; componente: string }) => {
      // As duas rotas devolvem o registro gravado; a tela usa o id para realçar a linha.
      const r = id
        ? await api.put(`api/biblioteca-componentes/${id}`, { componente }, foraDoProjeto())
        : await api.post("api/biblioteca-componentes", { componente }, foraDoProjeto())
      return (r?.data ?? null) as ComponenteDaBiblioteca | null
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biblioteca-componentes"] }),
  })
}

export function usarExcluirDaBiblioteca() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/biblioteca-componentes/${id}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biblioteca-componentes"] }),
  })
}

// ---------------------------------------------------------- posicionamentos

export type PosicionamentoPadrao = {
  id: number
  sistemaId: number
  apelido: string
  cor: string
  posicao: string
  ordem: number
}

export function usarPosicionamentos(sistemaId: number) {
  return useQuery({
    queryKey: ["posicionamentos-padrao", sistemaId] as const,
    enabled: sistemaId > 0,
    staleTime: 60_000,
    queryFn: () => obter<PosicionamentoPadrao[]>("api/posicionamentos-padrao", { sistemaId }),
  })
}

export type NovoPosicionamento = Omit<PosicionamentoPadrao, "id">

export function usarSalvarPosicionamento() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id?: number; dados: NovoPosicionamento }) => {
      const r = id
        ? await api.put(`api/posicionamentos-padrao/${id}`, dados, foraDoProjeto())
        : await api.post("api/posicionamentos-padrao", dados, foraDoProjeto())
      return (r?.data ?? null) as PosicionamentoPadrao | null
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posicionamentos-padrao"] }),
  })
}

export function usarExcluirPosicionamento() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/posicionamentos-padrao/${id}`, foraDoProjeto())
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["posicionamentos-padrao"] }),
  })
}
